import { getAuthToken, getBidderId, getUserData } from '@/redux/modules/user';
import api from '@/redux/api/conversationMessages';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getDeployment } from '@/redux/modules/config';
import { GlobalState } from '../rootReducer';
import { LOG_OUT_BIDDER } from './actions/user';
import union from 'lodash/union';
import uniqBy from 'lodash/uniqBy';

/* Action Types */
const LOAD_CONVERSATION_MESSAGES_FAIL = 'la/domain/loadMessages/LOAD_FAIL';
const LOAD_CONVERSATION_MESSAGES_REQUEST = 'la/domain/loadMessages/LOAD_REQUEST';
const LOAD_CONVERSATION_MESSAGES_SUCCESS = 'la/domain/loadMessages/LOAD_SUCCESS';

const SEND_CONVERSATION_MESSAGE_FAIL = 'la/domain/sendMessage/LOAD_FAIL';
const SEND_CONVERSATION_MESSAGE_REQUEST = 'la/domain/sendMessage/LOAD_REQUEST';
const SEND_CONVERSATION_MESSAGE_SUCCESS = 'la/domain/sendMessage/LOAD_SUCCESS';

const CONVERSATIONS_CREATE_DRAFT_REQUEST = 'CONVERSATIONS_CREATE_DRAFT_REQUEST';
const CONVERSATIONS_CREATE_DRAFT_SUCCESS = 'CONVERSATIONS_CREATE_DRAFT_SUCCESS';
const CONVERSATIONS_CREATE_DRAFT_FAIL = 'CONVERSATIONS_CREATE_DRAFT_FAIL';

const CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_REQUEST = 'CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_REQUEST';
const CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_SUCCESS = 'CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_SUCCESS';
const CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_FAIL = 'CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_FAIL';

const CLEAR_DRAFT = 'CLEAR_DRAFT';

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
    messageDrafts: {},
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;
    let draftAttachments;

    switch (action.type) {
        case SEND_CONVERSATION_MESSAGE_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.conversationId]),
            };
        case SEND_CONVERSATION_MESSAGE_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case SEND_CONVERSATION_MESSAGE_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (Array.isArray(action.payload)) {
                const messages = action.payload;
                existing[action.meta.conversationId] = uniqBy(
                    [...existing[action.meta.conversationId], ...messages],
                    'messageId'
                );
                loaded[action.meta.conversationId] = time;
                loading = difference(loading, [action.meta.conversationId]);
            } else {
                const message = action.payload;
                existing[action.meta.conversationId] = uniqBy(
                    [...existing[action.meta.conversationId], message],
                    'messageId'
                );
                loaded[action.meta.conversationId] = time;
                loading = difference(loading, [action.meta.conversationId]);
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case CONVERSATIONS_CREATE_DRAFT_SUCCESS:
            existing = cloneDeep(state.messageDrafts);
            draftAttachments = action.payload.attachments.map((attachment) => ({
                id: attachment.attachmentId,
                name: attachment.originalFilename,
            }));
            existing[action.payload.conversationId] = {
                draftAttachments,
                draftId: action.payload.draftId,
            };
            return {
                ...state,
                messageDrafts: existing,
            };
        case CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_SUCCESS:
            existing = cloneDeep(state.messageDrafts);
            draftAttachments = existing[action.meta.conversationId].draftAttachments.filter(
                (draftAttachment) => action.meta.attachmentName !== draftAttachment.name
            );
            existing[action.meta.conversationId].draftAttachments = draftAttachments;
            return {
                ...state,
                messageDrafts: existing,
            };
        case LOAD_CONVERSATION_MESSAGES_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.conversationId]),
            };
        case LOAD_CONVERSATION_MESSAGES_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CONVERSATION_MESSAGES_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (Array.isArray(action.payload)) {
                existing[action.meta.conversationId] = action.payload;
                loaded[action.meta.conversationId] = time;
                loading = difference(loading, [action.meta.conversationId]);
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case CLEAR_DRAFT:
            existing = cloneDeep(state.messageDrafts);
            existing[action.payload.conversationId] = {};
            return {
                ...state,
                messageDrafts: existing,
            };
        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };
        default:
            return state;
    }
}

const stateSelector = (state: GlobalState) => state.conversationMessages;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const draftsSelector = createSelector(stateSelector, (state) => state.messageDrafts);

const getMessages = createSelector([byIdSelector, idSelector], (byId, id) => {
    return byId[id] || [];
});

export const getConversationMessages: (state: any, selector: any) => any = (
    state: GlobalState,
    conversationId: number
) => {
    const userData = getUserData(state);
    return getMessages(state, conversationId).map((message) => {
        if (message?.sender?.type === 'bidder' && message?.sender?.id === userData.bidderId) {
            return {
                ...message,
                sender: {
                    ...message.sender,
                    initials: `${userData.firstName[0]}${userData.lastName[0]}`,
                    name: `${userData.firstName} ${userData.lastName}`,
                },
            };
        }
        return message;
    });
};

export const getDraftId: (state: any, selector: any) => any = createSelector(
    [draftsSelector, idSelector],
    (messageDrafts, id) => messageDrafts[id]?.draftId
);

const getDraftAttachments = createSelector(
    [draftsSelector, idSelector],
    (messageDrafts, id) => messageDrafts[id]?.draftAttachments || []
);

const getAttachmentId = createSelector(
    [draftsSelector, idSelector, idSelector],
    (messageDrafts, { attachmentName, conversationId }) => {
        const attachments = messageDrafts[conversationId]?.draftAttachments;
        const attachment = attachments.find((att) => att.name === attachmentName);

        return attachment.name ? attachment.id : 0;
    }
);

export const clearDraft = (conversationId: number) => (dispatch: Function) => {
    dispatch({
        payload: { conversationId },
        type: CLEAR_DRAFT,
    });
};

const loadConversationMessages = (conversationId: number) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATION_MESSAGES_REQUEST,
        });
        const response = await api.fetchConversationMessages({ authToken, bidderId, conversationId, deployment });
        return dispatch({
            meta: { actionTime: Date.now(), conversationId },
            payload: response.payload,
            type: LOAD_CONVERSATION_MESSAGES_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            meta: { conversationId },
            payload: error,
            type: LOAD_CONVERSATION_MESSAGES_FAIL,
        });
    }
};

export const sendConversationMessage =
    (conversationId: number, messageBody: any, draftId: number) => async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);
            dispatch({
                type: SEND_CONVERSATION_MESSAGE_REQUEST,
            });
            dispatch(clearDraft(conversationId));
            const response = await api.sendConversationMessage({
                authToken,
                bidderId,
                conversationId,
                deployment,
                draftId: Boolean(draftId) ? draftId : undefined,
                messageBody,
            });
            return dispatch({
                meta: { actionTime: Date.now(), conversationId },
                payload: response.payload,
                type: SEND_CONVERSATION_MESSAGE_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                meta: { conversationId },
                payload: error,
                type: SEND_CONVERSATION_MESSAGE_FAIL,
            });
        }
    };

export const fetchConversationMessages = (conversationId: number) => async (dispatch: Function) => {
    return dispatch(loadConversationMessages(conversationId));
};

const filterAttachedImages = (conversationId, newAttachments) => async (dispatch, getState) => {
    const state = getState();
    const draftAttachments = getDraftAttachments(state, conversationId);
    const draftAttachmentsNames = draftAttachments.map((att) => att.name);

    return newAttachments.filter((newAttachment) => {
        return !draftAttachmentsNames.includes(newAttachment.name);
    });
};

export const addAttachment =
    (conversationId: number, attachments: Array<any>, messageBody: string, draftId: number, sellerId?: number) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);
            // necessary to make sure we do not attach files that are already attached
            const filteredAttachments = await dispatch(filterAttachedImages(conversationId, attachments));
            if (!filteredAttachments.length) {
                return Promise.resolve({ error: false });
            }

            dispatch({
                type: CONVERSATIONS_CREATE_DRAFT_REQUEST,
            });
            const response = await api.addAttachment({
                attachments: filteredAttachments,
                authToken,
                bidderId,
                conversationId,
                deployment,
                draftId,
                messageBody,
                sellerId,
            });
            return dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: CONVERSATIONS_CREATE_DRAFT_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                payload: error,
                type: CONVERSATIONS_CREATE_DRAFT_FAIL,
            });
        }
    };

export const deleteAttachment =
    (attachmentName: string, conversationId: number) => async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);
            const attachmentId = getAttachmentId(state, { attachmentName, conversationId });

            dispatch({
                type: CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_REQUEST,
            });
            const response = await api.deleteDraftAttachment({
                attachmentId,
                authToken,
                bidderId,
                deployment,
            });
            return dispatch({
                meta: { actionTime: Date.now(), attachmentName, conversationId },
                payload: response.payload,
                type: CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                payload: error,
                type: CONVERSATIONS_DELETE_DRAFT_ATTACHMENT_FAIL,
            });
        }
    };
